import { db } from '../db/db';

export interface EInvoiceData {
  Version: string;
  TranDtls: { TaxSch: string; SupTyp: string; RegRev: string; IgstOnIntra: string };
  DocDtls: { Typ: string; No: string; Dt: string };
  SellerDtls: { Gstin: string; LglNm: string; Addr1: string; Loc: string; Pin: number; Stcd: string; };
  BuyerDtls: { Gstin: string; LglNm: string; Pos: string; Addr1: string; Loc: string; Pin: number; Stcd: string; };
  ItemList: {
    SlNo: string; PrdDesc: string; IsServc: string; HsnCd: string;
    Qty: number; Unit: string; UnitPrice: number; TotAmt: number;
    Discount: number; AssAmt: number;
    GstRt: number; IgstAmt: number; CgstAmt: number; SgstAmt: number;
    TotItemVal: number;
  }[];
  ValDtls: { AssVal: number; CgstVal: number; SgstVal: number; IgstVal: number; TotInvVal: number; };
}

export async function generateEInvoiceJSON(voucherId: number): Promise<EInvoiceData | null> {
  const voucher = await db.vouchers.get(voucherId);
  if (!voucher || voucher.deleted) return null;

  const company = await db.companies.get(voucher.companyId);
  if (!company) return null;

  const entries = await db.voucherEntries.where('voucherId').equals(voucherId).toArray();
  const inventoryEntries = await db.inventoryEntries.where('voucherId').equals(voucherId).toArray();

  const allLedgers = await Promise.all(entries.map(e => db.ledgers.get(e.ledgerId)));
  const ledgerMap = new Map(allLedgers.filter(Boolean).map(l => [l!.id!, l!]));

  const allGroups = await db.groups.where('companyId').equals(voucher.companyId).toArray();
  const groupMap = new Map(allGroups.map(g => [g.id!, g]));

  const partyEntry = entries.find(e => {
    const led = ledgerMap.get(e.ledgerId);
    if (!led) return false;
    const grp = groupMap.get(led.groupId!);
    return grp && (grp.name === 'Sundry Debtors' || grp.name === 'Sundry Creditors');
  });
  const partyLedger = partyEntry ? ledgerMap.get(partyEntry.ledgerId) : null;

  const taxEntries = entries.filter(e => {
    const led = ledgerMap.get(e.ledgerId);
    if (!led) return false;
    const grp = groupMap.get(led.groupId!);
    return grp && grp.name === 'Duties & Taxes';
  });

  let cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
  taxEntries.forEach(e => {
    const led = ledgerMap.get(e.ledgerId);
    const name = led?.name?.toLowerCase() || '';
    if (name.includes('cgst')) cgstTotal += e.amount;
    else if (name.includes('sgst')) sgstTotal += e.amount;
    else if (name.includes('igst')) igstTotal += e.amount;
  });

  const itemEntries = entries.filter(e => {
    const led = ledgerMap.get(e.ledgerId);
    if (!led) return false;
    const grp = groupMap.get(led.groupId!);
    return grp && (grp.name === 'Sales Accounts' || grp.name === 'Purchase Accounts');
  });
  const assessableValue = itemEntries.reduce((s, e) => s + e.amount, 0);

  const items: EInvoiceData['ItemList'] = [];
  if (inventoryEntries.length > 0) {
    for (let i = 0; i < inventoryEntries.length; i++) {
      const ie = inventoryEntries[i];
      const stockItem = await db.stockItems.get(ie.stockItemId);
      const unit = stockItem?.unitId ? await db.units.get(stockItem.unitId) : null;
      const lineAmt = ie.quantity * ie.rate;
      const gstRate = stockItem?.gstRate || 18;
      const isInter = igstTotal > 0;
      items.push({
        SlNo: String(i + 1),
        PrdDesc: stockItem?.name || 'Item',
        IsServc: 'N',
        HsnCd: stockItem?.hsn || '',
        Qty: ie.quantity,
        Unit: unit?.symbol || 'NOS',
        UnitPrice: ie.rate,
        TotAmt: lineAmt,
        Discount: 0,
        AssAmt: lineAmt,
        GstRt: gstRate,
        IgstAmt: isInter ? lineAmt * gstRate / 100 : 0,
        CgstAmt: !isInter ? lineAmt * (gstRate / 2) / 100 : 0,
        SgstAmt: !isInter ? lineAmt * (gstRate / 2) / 100 : 0,
        TotItemVal: lineAmt + (isInter ? lineAmt * gstRate / 100 : lineAmt * gstRate / 100),
      });
    }
  } else {
    const isInter = igstTotal > 0;
    const gstRate = assessableValue > 0 ? Math.round(((cgstTotal + sgstTotal + igstTotal) / assessableValue) * 100) : 18;
    items.push({
      SlNo: '1', PrdDesc: voucher.type + ' Invoice', IsServc: 'Y', HsnCd: '999999',
      Qty: 1, Unit: 'NOS', UnitPrice: assessableValue, TotAmt: assessableValue,
      Discount: 0, AssAmt: assessableValue, GstRt: gstRate,
      IgstAmt: igstTotal, CgstAmt: cgstTotal, SgstAmt: sgstTotal,
      TotItemVal: assessableValue + cgstTotal + sgstTotal + igstTotal,
    });
  }

  const fmtDate = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const companyAny = company as any;
  const sellerState = companyAny.state || '';
  const sellerStateCode = INDIAN_STATE_CODES[sellerState] || '';
  const buyerState = partyLedger?.state || sellerState;
  const buyerStateCode = INDIAN_STATE_CODES[buyerState] || sellerStateCode;

  return {
    Version: '1.1',
    TranDtls: { TaxSch: 'GST', SupTyp: 'B2B', RegRev: 'N', IgstOnIntra: 'N' },
    DocDtls: { Typ: voucher.type === 'Sales' ? 'INV' : voucher.type === 'Credit Note' ? 'CRN' : 'DBN', No: voucher.voucherNumber, Dt: fmtDate(voucher.date) },
    SellerDtls: {
      Gstin: companyAny.gstin || '', LglNm: company.name, Addr1: companyAny.address || '',
      Loc: sellerState, Pin: parseInt(companyAny.pincode) || 0, Stcd: sellerStateCode,
    },
    BuyerDtls: {
      Gstin: partyLedger?.gstin || '', LglNm: partyLedger?.name || '',
      Pos: buyerStateCode, Addr1: partyLedger?.address || '',
      Loc: buyerState, Pin: parseInt(partyLedger?.pincode || '0') || 0, Stcd: buyerStateCode,
    },
    ItemList: items,
    ValDtls: {
      AssVal: assessableValue, CgstVal: cgstTotal, SgstVal: sgstTotal,
      IgstVal: igstTotal, TotInvVal: assessableValue + cgstTotal + sgstTotal + igstTotal,
    },
  };
}

const INDIAN_STATE_CODES: Record<string, string> = {
  'Jammu and Kashmir': '01', 'Himachal Pradesh': '02', 'Punjab': '03', 'Chandigarh': '04',
  'Uttarakhand': '05', 'Haryana': '06', 'Delhi': '07', 'Rajasthan': '08',
  'Uttar Pradesh': '09', 'Bihar': '10', 'Sikkim': '11', 'Arunachal Pradesh': '12',
  'Nagaland': '13', 'Manipur': '14', 'Mizoram': '15', 'Tripura': '16',
  'Meghalaya': '17', 'Assam': '18', 'West Bengal': '19', 'Jharkhand': '20',
  'Odisha': '21', 'Chhattisgarh': '22', 'Madhya Pradesh': '23', 'Gujarat': '24',
  'Dadra and Nagar Haveli': '26', 'Maharashtra': '27', 'Andhra Pradesh': '37',
  'Karnataka': '29', 'Goa': '30', 'Lakshadweep': '31', 'Kerala': '32',
  'Tamil Nadu': '33', 'Puducherry': '34', 'Andaman and Nicobar Islands': '35',
  'Telangana': '36', 'Ladakh': '38',
};
